import validate from 'validate.js';
import _camelCase from 'lodash/camelCase';
import _cloneDeep from 'lodash/cloneDeep';
import _drop from 'lodash/drop';
import _find from 'lodash/find';
import _forEach from 'lodash/forEach';
import _groupBy from 'lodash/groupBy';
import _has from 'lodash/has';
import _isArray from 'lodash/isArray';
import _isEmpty from 'lodash/isEmpty';
import _isInteger from 'lodash/isInteger';
import _isObject from 'lodash/isObject';
import _last from 'lodash/last';
import _map from 'lodash/map';
import _merge from 'lodash/merge';
import _reduce from 'lodash/reduce';
import _reject from 'lodash/reject';
import _replace from 'lodash/replace';
import _set from 'lodash/set';
import _size from 'lodash/size';
import _split from 'lodash/split';
import _transform from 'lodash/transform';
import RegexUtils from './RegexUtils';

// --- //
export const mimeTypesCSV = [
  'text/comma-separated-values',
  'text/csv',
  'application/csv',
  'application/excel',
  'application/vnd.ms-excel',
  'application/vnd.msexcel',
  'text/anytext',
];

export const mimeTypeJSON = [
  'application/json',
];

// --- //
export const transformServerError = (messagesOrChildren) => {
  if (_isArray(messagesOrChildren)) {
    // messagesOrChildren is an aray of children => transform all children
    if (_isObject(messagesOrChildren[0])) {
      // Find the first not empty child and transform it
      const firstNotEmptyChild = _find(messagesOrChildren, (c) => !_isEmpty(c));
      return _transform(firstNotEmptyChild, (result, childValue, childKey) => {
        _set(result, _camelCase(childKey), transformServerError(childValue, _camelCase(childKey)));
        return result;
      }, {});
    }
    // messagesOrChildren is an array of messages => return them camel cased
    return _map(messagesOrChildren, (msg) => _camelCase(msg));
  }
  // messagesOrChildren is an object: transform its children
  return _transform(messagesOrChildren, (result, childValue, childKey) => {
    _set(result, _camelCase(childKey), transformServerError(childValue, _camelCase(childKey)));
    return result;
  }, {});
};

export const buildServerErrors = (operationError) => {
  if (_isEmpty(operationError) || !_has(operationError, 'errors')) {
    return null;
  }

  const { errors, messageValuesExtractors } = operationError;

  // errors is either a string (= global error), an object like:
  // {
  //   errors: {
  //     name: ["can't be blank"],
  //     address: {
  //       zip_code: ["can't be blank"],
  //       city: ["bad format"]
  //     },
  //     triggers: [
  //       {},
  //       {},
  //       {
  //         trigger_equipment: {
  //           name: ["can't be blank"]
  //         }
  //       }
  //     ]
  //   },
  //   status: 400
  // }
  // or, for GraphQL requests, a non-empty array like:
  // `extensions` is used for client errors (like 4xx HTTP errors), on mutations only
  // [
  //   {
  //     locations: [{column, line}],
  //     message: "not found",
  //     path: ["mutationOrQueryName", "user", "permission", "0", "desc"]
  //   },
  //   {
  //     extensions: {
  //       path: ["password"]
  //     },
  //     locations: [{column, line}],
  //     message: "can't be blank",
  //     path: ["mutationName"]
  //   }
  // ]
  // case of global error (no extensions, lenght of path = 1):
  // [
  //   {
  //     locations: [{column, line}],
  //     message: "Bad credentials",
  //     path: ["login"]
  //   }
  // ]

  // messageValuesExtractors (optional), is an object of values to be injected in error message, with format:
  // field: [
  //   {
  //     key, // will be the key of the value in message
  //     pattern, // Regexp to find the value in server message
  //     setTransform, // Optional function to transform value
  //   }
  // ]

  // Array: GraphQL error
  if (_isArray(errors)) {
    // Treat global errors: only one error, 0 or 1 element in path, no `extensions` attribute
    if (_size(errors) === 1 && _size(errors[0].path) === 1 && !_has(errors[0], 'extensions')) {
      return {
        global: `${errors[0].path[0]}.${_camelCase(errors[0].message)}`,
      };
    }

    // Other cases: we have errors on fields, so parse and compute them
    return _reduce(errors, (result, value) => {
      const { extensions, message, path } = value;
      // Treat extensions
      if (extensions) {
        const { path: extensionsPath } = extensions;
        // Format extension path
        // Note: remove from path indexes (= numbers)
        const formattedPath = _map(_reject(extensionsPath, _isInteger), _camelCase);
        const pathKey = _last(formattedPath);
        // Format message
        let formattedMessageId = message;
        const formattedMessageValues = {};
        // Extract dynamic values if any
        if (messageValuesExtractors) {
          const messageValuesExtractor = messageValuesExtractors[pathKey];
          if (messageValuesExtractor) {
            _forEach(messageValuesExtractor, ({ key, pattern, setTransform }) => {
              // Find the value according with given pattern
              const match = formattedMessageId.match(pattern);
              if (_size(match) > 1) {
                const matchedValue = match[1];
                // Remove from message the value
                formattedMessageId = _replace(formattedMessageId, match[0], '');
                // Store value
                formattedMessageValues[key] = { value: matchedValue, setTransform };
              }
            });
          }
        }
        _set(result, ['details', ...formattedPath], [{ id: _camelCase(formattedMessageId), values: formattedMessageValues }]);
      } else if (_size(path) > 1) {
        // Path contains in its first element the name of query/mutation
        // Remove it to get the field concerned
        const fieldPath = _drop(path);
        // Note: remove from path indexes (= numbers)
        _set(result, ['details', ..._map(_reject(fieldPath, _isInteger), _camelCase)], [_camelCase(message)]);
      }
      return result;
    }, {
      global: 'form',
      details: {},
    });
  }

  // Object : REST error on fields
  if (_isObject(errors)) {
    // Transform the example above into an object like:
    // {
    //   global: 'form',
    //   details: {
    //     name: ['cantBeBlank'],
    //     address: {
    //       zipCode: ["cantBeBlank"],
    //       city: ["badFormat"]
    //     },
    //     triggers: {
    //       triggerEquipment: {
    //         name: ['cantBeBlank']
    //       }
    //     }
    //   }
    // }
    return _transform(errors, (result, value, key) => {
      // Note: we must camelCase keys to standardize locale and server errors
      _set(result, ['details', _camelCase(key)], transformServerError(value));
      return result;
    }, {
      global: 'form',
      details: {},
    });
  }

  // Other: global error
  return {
    global: _camelCase(errors),
  };
};

export const buildFormErrors = (inputs, constraints) => {
  if (_isEmpty(inputs) || _isEmpty(constraints)) {
    return null;
  }

  // Validate inputs according to constraints
  const validatedErrors = validate(inputs, constraints, { format: 'custom' });
  if (_isEmpty(validatedErrors)) {
    return null;
  }

  // Check all constraints in form cover an input
  const constraintsKeys = Object.keys(constraints);
  const inputsKeys = Object.keys(inputs);
  const constraintsNotLinkedToInput = constraintsKeys.filter((constraintKey) => !inputsKeys.includes(constraintKey));
  // eslint-disable-next-line no-console
  if (constraintsNotLinkedToInput.length > 0) console.error('Some constraints passed to form do not cover an input :', constraintsNotLinkedToInput);

  // validatedErrors is an array like:
  // [
  //   {field: 'name', message: 'cantBeBlank'},
  //   {field: 'name', message: 'badFormat'},
  //   {field: 'timezone', message: 'cantBeBlank'}
  // ]
  // That we must transform into an object like:
  // {
  //   global: 'form',
  //   details: {
  //     name: ['cantBeBlank', 'badFormat'],
  //     timezone: ['cantBeBlank']
  //   }
  // }
  return (
    _transform(
      _groupBy(validatedErrors, 'field'), (result, value, key) => {
        // After _groupBy, each element is like
        // {
        //   name: [
        //     field: 'name', message: 'cantBeBlank',
        //     field: 'name', message: 'badFormat'
        //   ]
        // }
        // Map all messages in a single array
        _set(result, ['details', key], _map(value, ({ message }) => message));
        return result;
      }, {
        global: 'form',
        details: {},
      },
    )
  );
};

export const buildGlobalErrors = (globalMessage) => ({
  global: _camelCase(globalMessage),
});

export const completeFormErrors = (errors, path, messages) => {
  let newErrors = {};
  if (errors) {
    // Do not mutate initial errors!
    newErrors = _cloneDeep(errors);
  } else {
    newErrors = {
      global: 'form',
      details: {},
    };
  }
  return _set(newErrors, ['details', ..._split(path, '.')], messages);
};

export const removeClientErrors = (errors, errorsToRemove) => {
  errorsToRemove.forEach((errorToRemove) => {
    if (errors[errorToRemove]) {
      // eslint-disable-next-line no-param-reassign
      delete errors[errorToRemove];
    }

    const thereAreNoOtherClientError = Object.keys(errors).length === 1 && errors.global === 'form';
    if (thereAreNoOtherClientError) {
      // eslint-disable-next-line no-param-reassign
      delete errors.global;
    }
  });
};

export const computeErrors = (localErrors, operationError) => {
  // Build full errors with local errors and server errors
  let errors;
  if (!_isEmpty(localErrors)) {
    errors = _merge(errors, localErrors);
  }
  // Note that `global` attribute may be updated by server built errors
  if (!_isEmpty(operationError)) {
    errors = _merge(errors, buildServerErrors(operationError));
  }

  return errors;
};

// -- Constaints -- //
// General configuration of validate lib
validate.formatters.custom = (errors) => (_map(errors, (error) => (
  {
    field: _camelCase(_last(_split(error.attribute, '.'))),
    message: error.options.message,
  }
)));

validate.validators.customEmail = (value, options) => {
  if (_isEmpty(value)) {
    return null;
  }
  const { message } = options;
  return validate.single(value, { email: { message } });
};

validate.validators.customLicensePlate = (value, options) => {
  if (_isEmpty(value)) {
    return null;
  }
  const { message } = options;
  return validate.single(value, {
    format: {
      pattern: RegexUtils.licensePlate,
      message,
    },
  });
};

const presence = {
  presence: {
    message: 'cantBeBlank',
    allowEmpty: false,
  },
};

export const signinConstraints = {
  email: {
    ...presence,
    customEmail: { message: 'badFormat' },
  },
  password: presence,
};

export const forgotPasswordConstraints = {
  email: {
    ...presence,
    customEmail: { message: 'badFormat' },
  },
};

export const installationConstraints = {
  name: presence,
};

export const entityConstraints = {
  name: presence,
  companyId: presence,
};

export const doorConstraints = {
  name: presence,
  usage: presence,
  type: presence,
};

export const sourceConstraints = {
  name: presence,
  type: presence,
};

export const deviceConstraints = {
  companyId: presence,
};

export const deviceConfigConstraints = {
  serverUrl: {
    ...presence,
    // format: {
    //   pattern: /^((https?|ftp)?:\/\/[^\s/$.?#].[^\s]*)|((?!0)(?!.*\.$)((1?\d?\d|25[0-5]|2[0-4]\d)(\.|$)){4})$/i,
    //   message: 'badFormat'
    // }
  },
  monitoringTimer: presence,
  ignorePlateDuration: presence,
  tcpPort: presence,
  udpPort: presence,
  periodicSynchronizationTimer: presence,
  equipmentsNetworkIp: {
    ...presence,
    format: {
      pattern: RegexUtils.ipv4CidrNotation,
      message: 'badFormat',
    },
  },
};

export const deviceSlaveConstraints = {
  companyId: presence,
};

export const installationDeviceConstraints = {
  name: presence,
};

export const installationDeviceAddConstraints = {
  device: presence,
  name: presence,
};

export const installationDeviceReplaceConstraints = {
  device: presence,
};

export const installationDeviceSlaveAddConstraints = {
  deviceSlave: presence,
  name: presence,
};

export const installationDeviceSlaveUpdateConstraints = {
  name: presence,
};

export const installationDeviceSlaveReplaceConstraints = {
  deviceSlave: presence,
};

export const scenarioConstraints = {
  device: presence,
  name: presence,
};

export const customerConstraints = {
  entityId: presence,
  lastname: presence,
  email: {
    customEmail: { message: 'badFormat' },
  },
};

export const accessWayConstraints = {
  type: presence,
  desc: presence,
};

export const keypadCodeConstraints = {
  keypadCode: {
    ...presence,
    format: {
      pattern: RegexUtils.keypadCode,
      message: 'mustBe6Digits',
    },
  },
};

export const licensePlateConstraints = {
  licensePlate: {
    ...presence,
    format: {
      pattern: RegexUtils.licensePlate,
      message: 'badFormat',
    },
  },
};

export const rightConstraints = {
  scenarioId: presence,
  scenarioTriggerId: presence,
};

export const planningConstraints = {
  entityId: presence,
  name: presence,
};

export const simcardConstraints = {
  groupId: presence,
};

export const simcardImportConstraints = {
  groupId: presence,
  validLines: {
    numericality: {
      greaterThan: 0,
      message: 'atLeastOne',
    },
  },
};

export const simcardGroupConstraints = {
  name: presence,
};

export const companyConstraints = {
  name: presence,
};

export const userConstraints = {
  locale: presence,
  firstname: presence,
  lastname: presence,
  email: {
    ...presence,
    customEmail: { message: 'badFormat' },
  },
};
export const userWithPasswordConstraints = {
  ...userConstraints,
  password: presence,
};
export const recoverPasswordConstraints = {
  password: presence,
};
export const accountLinkRegisterConstraints = {
  lastname: presence,
  firstname: presence,
  email: {
    ...presence,
    customEmail: { message: 'badFormat' },
  },
  password: presence,
  phone: presence,
  locale: presence,
};
export const accountLinkLoginConstraints = {
  email: {
    ...presence,
    customEmail: { message: 'badFormat' },
  },
  password: presence,
};

export const meConstraints = {
  locale: presence,
  lastname: presence,
  firstname: presence,
  email: {
    ...presence,
    customEmail: { message: 'badFormat' },
  },
};

export const profileConstraints = {
  product: presence,
  name: presence,
};

export const deviceBatchConstraints = {
  name: presence,
  deviceModelId: presence,
  date: presence,
  volume: {
    numericality: {
      greaterThan: 0,
      message: 'atLeastOne',
    },
  },
};

export const deviceSmsConstraints = {
  type: presence,
};

export const distributionConstraints = {
  version: presence,
  type: presence,
};

export const ethernetEquipmentConstraints = {
  name: presence,
  mac: {
    ...presence,
    format: {
      pattern: RegexUtils.macAddress,
      message: 'badFormat',
    },
  },
  ip: {
    ...presence,
    format: {
      pattern: RegexUtils.ipv4,
      message: 'badFormat',
    },
  },
};

export const antipassbackConstraints = {
  name: presence,
};

export const dailyCodeConstraints = {
  entityId: presence,
  begin: presence,
  end: presence,
  prefixLastname: presence,
};

// CPM
export const cpmCarparkContactConstraints = {
  email: {
    customEmail: { message: 'badFormat' },
  },
};

export const cpmCarparkOnDemandSettingsConstraints = {
  mode: presence,
};
export const cpmCarparkOnDemandSettingsAutoCatConstraints = {
  parkingCategoryId: presence,
};
export const cpmCarparkOnDemandSettingsSpecCatConstraints = {
  priceGridId: presence,
};

export const cpmPlanningConstraints = {
  name: presence,
};

export const cpmParkingCategoryConstraints = {
  name: presence,
  color: presence,
};

export const cpmCarparkSettingsSpacesConstraints = {
  bookingCapacity: presence,
  assignedCapacity: presence,
};

export const cpmParkingCategoryPriceGridConstraints = {
  priceGridId: presence,
  discount: presence,
  priceGridCalculationMode: presence,
};

export const cpmPriceGridParkingCategoryConstraints = {
  parkingCategoryId: presence,
  discount: presence,
  priceGridCalculationMode: presence,
};

export const cpmPriceGridConstraints = {
  name: presence,
};

export const cpmPriceGridTierConstraints = {
  duration: presence,
  amountInclTax: presence,
};

export const cpmCustomerConstraints = {
  parkingCategoryId: presence,
  lastname: presence,
  email: {
    customEmail: { message: 'badFormat' },
  },
};

export const cpmParkingAddConstraints = {
  customer: presence,
  spacesQuantity: presence,
  insideQuantity: presence,
};

export const cpmParkingUpdateConstraints = {
  begin: presence,
  end: presence,
};

export const cpmBookingAddConstraints = {
  begin: presence,
  customer: presence,
  end: presence,
  insideQuantity: presence,
  spacesQuantity: presence,
};

export const cpmBookingConstraints = {
  begin: presence,
  end: presence,
  licensePlate: {
    ...presence,
    customLicensePlate: { message: 'badFormat' },
  },
};

export const signupConstraints = {
  email: {
    ...presence,
    customEmail: { message: 'badFormat' },
  },
  lastname: presence,
  firstname: presence,
  password: presence,
  passwordConfirmation: presence,
  phone: presence,
};

export const accountSetStatusConstraints = {
  cause: presence,
};

export const cpmOnDemandAccessLicensePlateConstraints = {
  licensePlate: {
    ...presence,
    customLicensePlate: { message: 'badFormat' },
  },
};

export const cpmCheckoutOnDemandAccessConstraints = {
  name: presence,
  email: {
    ...presence,
    customEmail: { message: 'badFormat' },
  },
};

export const cpmOnDemandAccessSendPriceGridConstraints = {
  email: {
    ...presence,
    customEmail: { message: 'badFormat' },
  },
};

export const cpmOnDemandAccessTerminateConstraints = {
  comments: presence,
};

export const cpmOnDemandAccessUpdateConstraints = {
  begin: presence,
  licensePlate: {
    ...presence,
    customLicensePlate: { message: 'badFormat' },
  },
};

export const cpmAddressConstraints = {
  city: presence,
  country: presence,
  street: presence,
  zipCode: presence,
};

export const cpmPartnerFinancialInformationConstraints = {
  emailForCommissionInvoices: {
    ...presence,
    customEmail: { message: 'badFormat' },
  },
  europeanVatNumber: presence,
  iban: presence,
  nationalIdentifier: presence,
  street: presence,
  city: presence,
  zipCode: presence,
  country: presence,
};

export const workspaceConstraints = {
  name: presence,
};

export const serviceFeeRuleConstraints = {
  name: presence,
  value: presence,
};

export const slugConstraints = {
  domain: presence,
};



// WEBPACK FOOTER //
// ./utils/Forms.js